import { NEW_ID_SENTINEL } from '@/cascade/types';
import { getRouter, isRouterRegistered } from '@/const/routerHandle';
import { isColumnDraftState } from '@/composables/useColumnDraftState';
import { draftDefinition, drafts, isDraftDirty } from '@/stores/draftDefinitions';
import {
  draftZoneAvailable,
  graduationMemory,
  graduationSignal,
  mintNextColumnId,
  planGraduation,
  planSettle,
  surveyDraftColumns,
  type RecordedSighting,
} from '@/stores/draftGraduationPolicy';
import { activeDraftKey, draftKeyType, instanceDraftKey, type DraftKey } from '@/stores/draftKey';
import { sidebars, type SidebarType } from '@/registries/sidebars';
import { useCascadeStore } from '@/stores/cascadeStore';
import { useDraftStateStore } from '@/stores/draftStateStore';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

/**
 * One create panel living in the draft zone — the strip to the RIGHT of the
 * cascade columns (ADR-0029 §G).
 *
 * A zone instance is NOT a cascade column: it has no place in the tree, no
 * segment in the URL and no parent. That is what lets a page hold several of the
 * same type at once, which a `type:id` URL cannot express (two
 * `createPatient:new` segments are one and the same column to the reducer).
 *
 * The price is that the opener's props have to be frozen here at creation rather
 * than re-derived from ancestor columns on every mount, which is why `props` is a
 * snapshot. For a root create flow (`createPatient` declares no `parentIds`)
 * there is nothing to lose; a type that reads prefill from an ancestor keeps that
 * prefill as the value it had when the user started typing, which is also the
 * only reading that survives two instances under two different records.
 */
export interface DraftInstance {
  /**
   * Opaque and stable for the instance's life. Doubles as its `draftStateStore`
   * key, so the panel's buffer, projection and baseline all hang off this one
   * string. Never reused, never derived from the position — the ORDINAL is
   * positional (see `ordinalOf`), the identity is not.
   */
  id: DraftKey;
  type: SidebarType;
  /** `route.name` of the page this instance belongs to and is only shown on. */
  routeName: string;
  /** The opener's props, frozen at creation. */
  props: Record<string, unknown> | null;
  /** Drawn as a rail rather than a full column. */
  minimized: boolean;
  /**
   * For an IDENTIFIED type (`DraftDefinition.keyFor`), the key its column had while
   * it was in the cascade — `customFormPlayer:<form>:<patient>`.
   *
   * Load-bearing rather than a label: reopening that same form must find this parked
   * draft and expand it instead of adding a second column. The panel persists its
   * answers itself, keyed by form and patient, so two live panels for one identity
   * would read and write the SAME answers and diverge on everything else (which step
   * each is on, which of them the ✕ discards). Absent for the create flows, whose
   * generations are distinct columns by construction.
   */
  identityKey?: DraftKey;
  /**
   * A panel opened FROM this draft and shown beside it — today the form editor's
   * "Vorschau".
   *
   * It is not a draft itself: it owns no entered values, closes without a prompt, and
   * carries no ordinal. It lives here rather than in the cascade because its owner
   * does: a parked draft is not in the URL, so there is nothing for a child COLUMN to
   * dock under, and one opened at root would appear on the far side of the board from
   * the draft it belongs to. Dying with its owner falls out of being a field on it.
   */
  child?: DraftZoneChildSpec;
  /**
   * The header its column was showing when it graduated — e.g.
   * `(Max Mustermann) Anamnesebogen`.
   *
   * Frozen with the props, and for the same reason: a parked draft has no panel, so
   * nothing can resolve a name from an id any more. The alternative — looking the
   * record up from the store — would put a query behind a label that renders on every
   * page, to recover something the panel already knew.
   *
   * Absent for a create flow, which has no record to name yet, and whenever the panel
   * never published a header. `labelFor` falls back to the declared draft title.
   */
  label?: string;
}

/** What a draft shows beside itself — see {@link DraftInstance.child}. */
export interface DraftZoneChildSpec {
  type: SidebarType;
  props: Record<string, unknown> | null;
}

/**
 * The Entwürfe a session is holding — one entry per parked create panel (TM-9455).
 *
 * A draft outlives the column that shows it: leaving the page unmounts the cascade,
 * but the instance here (and the buffer behind it) survives, so the user can walk away
 * and come back. It is deliberately in-memory only — a reload clears everything, which
 * is the same promise the buffer store makes.
 *
 * A draft belongs to the page it was started on. Elsewhere it is invisible and
 * announces itself only through a dot on that page's menu item; there is no floating
 * rail following the user around the app.
 *
 * TWO WRITERS TOUCH A DRAFT'S VALUES, by design (§H-1). The seam (`useDraftForm`)
 * writes them while a panel is mounted; this store writes them when a draft GRADUATES
 * or is thrown away, which both happen with no panel to ask. Routing the seam through
 * here instead would drag the router and the cascade into the dependency graph of a
 * form that only wants a buffer.
 */
export const useDraftStore = defineStore('draft', () => {
  const currentRouteName = computed<string>(() =>
    String(getRouter().currentRoute.value.name ?? ''),
  );

  // ---------------------------------------------------------------------------
  // Draft zone (ADR-0029 §G). Several create panels of the same type, side by
  // side to the right of the cascade — held here instead of in the URL.
  // ---------------------------------------------------------------------------

  const instances = ref<DraftInstance[]>([]);
  // Monotonic, never reused. Only mints the opaque id; the number the user sees
  // is positional and computed separately (see `ordinalOf`).
  let idCounter = 0;

  /**
   * Throw one draft's values away.
   *
   * Straight to the buffer store. Every type but one used to declare its own
   * `discard` in the registry, i.e. this store reached a sibling store by way of a
   * policy table to touch state one layer over, and eight of the nine entries wrote
   * out the same two lines to do it. What a type can still say is where ELSE its user
   * input lives, which is the hook below and exactly one type uses it (§H-4).
   *
   * Reaches the values with no panel mounted, which is the point: closing an instance
   * from the zone, graduating one that turned out to hold nothing, and the ✕ on a
   * panel that is about to unmount all arrive here.
   *
   * The page-change settler below is deliberately NOT one of them. It empties the
   * buffers of PRISTINE instances, and running `onDiscard` over those would clear the
   * questionnaire's stored answers on a plain navigation — its user input lives in
   * localStorage, and a clean buffer does not mean an unanswered form (§G-bis). That
   * asymmetry predates this slice; narrowing it means deciding whether answers outlive
   * the instance that showed them, which is a question for §G-bis and not for a rename.
   */
  function discard(key: DraftKey): void {
    useDraftStateStore().reset(key);
    draftDefinition(draftKeyType(key))?.onDiscard?.(key);
  }

  /**
   * Close one instance and throw its entered values away.
   *
   * Always discards, pristine or not: a create panel's buffer can hold POSITION as
   * well as data (which wizard step, which Fall), and leaving that behind would
   * make the next open resume a flow the user has closed. Callers that ask before
   * destroying work do so first — this is the destructive half only.
   */
  function remove(id: DraftKey): void {
    const instance = instances.value.find((entry) => entry.id === id);
    if (!instance) return;
    discard(id);
    // The reported name goes with it. A label outliving its draft would be handed to
    // the next panel of the same identity — the same form for the same patient, opened
    // fresh weeks later — which would then park under a name resolved from data nobody
    // has looked at since.
    graduationMemory.draftLabels.delete(instance.identityKey ?? activeDraftKey(instance.type));
    graduationMemory.draftLabels.delete(instance.id);
    instances.value = instances.value.filter((entry) => entry.id !== id);
  }

  /**
   * Which instances hold unsaved work. A getter rather than an `isDirty(id)`
   * action for the same reason `draftStateStore.dirtyKeys` is one:
   * `createTestingPinia` stubs actions to `undefined`, which would report every
   * badge, rail colour and menu dot as pristine.
   */
  const dirtyInstanceIds = computed<ReadonlySet<DraftKey>>(() => {
    const dirty = new Set<DraftKey>();
    for (const instance of instances.value) {
      if (isDraftDirty(instance.type, instance.id)) dirty.add(instance.id);
    }
    return dirty;
  });

  /**
   * Index of the cascade column that OPENS the draft region, or `null` when the
   * region starts in the zone (or does not exist).
   *
   * The active create column is an ordinary column until the user puts something in
   * it — and a draft from that moment on, even though it graduates into the zone only
   * later (§G). It should read as one from that moment too, so the boundary moves to
   * its LEFT rather than waiting for the graduation. Adding it costs the columns no
   * movement: the 30px comes out of the elastic list pane, not out of their slots.
   *
   * Answered here rather than in the strip because the strip cannot see dirtiness and
   * the zone cannot see the cascade — and because both need the SAME answer, or the
   * boundary would be drawn twice.
   */
  const activeDraftColumnIndex = computed<number | null>(() => {
    const stack = useCascadeStore().stack;
    const index = stack.findIndex((column) => {
      // Zone types only: a draft region exists to hold panels that can outlive their
      // column. The identified/generational question itself is the shared one.
      if (!draftDefinition(column.type)?.zone) return false;
      return isColumnDraftState(stack, column);
    });
    return index === -1 ? null : index;
  });

  /**
   * Does any of these routes hold a draft? Drives the menu dot — one dot, never a
   * count: it answers "did I leave something here?", and a number beside a menu
   * item reads as an unread counter, i.e. as a task the user owes someone.
   *
   * Two clauses, and they are the two PLACES a draft can be rather than two
   * mechanisms: parked in the zone, or still the active create column.
   */
  function hasDraftOnRoutes(routeNames: readonly string[]): boolean {
    return (
      instances.value.some(
        (entry) => routeNames.includes(entry.routeName) && dirtyInstanceIds.value.has(entry.id),
      ) ||
      // The ACTIVE create column counts too, once it holds work. It is a draft from its
      // first entered value and only graduates later, so without this the item the user
      // is standing on is the one item WITHOUT a dot — the dot appeared a moment later,
      // when they moved on and it graduated. Only meaningful for the current route:
      // elsewhere there is no active column of that page left to ask about, because
      // leaving is what graduates it.
      //
      // Router-guarded: this clause is the only part of the answer that needs the
      // cascade, and the menu asks the question from tests and contexts that have no
      // router. Nothing else here does, and demanding one for the whole answer would be
      // a constraint the store never had (same reasoning as the settling watcher).
      (isRouterRegistered() &&
        activeDraftColumnIndex.value !== null &&
        routeNames.includes(currentRouteName.value))
    );
  }

  /** The instances to draw right now: this page's, newest leftmost. */
  const zoneInstances = computed<DraftInstance[]>(() =>
    instances.value.filter((entry) => entry.routeName === currentRouteName.value),
  );

  /**
   * The number the user sees, counted among the SAME type on this page — 1-based,
   * gapless, derived from position rather than stored. Closing the middle one
   * renumbers the rest, and so does adding one, since the newest goes to the front:
   * the digit says "the second of the ones you can see", not "instance #7 of this
   * session". Returns 0 for the only instance of its type, which is the signal to
   * show no number at all — a lone "Patient anlegen (1)" would be pure noise.
   */
  function ordinalOf(id: DraftKey): number {
    const instance = instances.value.find((entry) => entry.id === id);
    if (!instance) return 0;
    const siblings = zoneInstances.value.filter((entry) => entry.type === instance.type);
    if (siblings.length < 2) return 0;
    return siblings.findIndex((entry) => entry.id === id) + 1;
  }

  /**
   * The draft's name plus its positional number, e.g. `Patient anlegen (2)`.
   *
   * Prefers the definition's `draftTitle` over the registry title: a panel that both
   * creates and edits carries a neutral registry title ("Leistungsdetails"), and a
   * parked draft is always the create side of it.
   */
  function labelFor(id: DraftKey): string {
    const instance = instances.value.find((entry) => entry.id === id);
    if (!instance) return '';
    // The header the panel itself showed wins: it names the RECORD (whose
    // questionnaire this is), which is the half that tells two parked drafts of one
    // type apart. Only when there is none does the declared title stand in — a type
    // that parks both create AND edit drafts (`customFormDetail`) picks from its own
    // frozen props there, everything else has one name for all of them.
    const declared = draftDefinition(instance.type)?.draftTitle;
    const title =
      instance.label ||
      (typeof declared === 'function' ? declared(instance.props ?? {}) : declared) ||
      sidebars.find((sidebar) => sidebar.type === instance.type)?.title ||
      '';
    // A named draft is not numbered: the name is what tells it apart, and "(2)" after
    // it would answer a question the name already answered. The ordinal is the fallback
    // for drafts that never reported one — an untouched create column has nothing to
    // call itself yet.
    if (instance.label) return title;
    const ordinal = ordinalOf(id);
    return ordinal === 0 ? title : `${title} (${ordinal})`;
  }

  /**
   * Show a panel beside one draft, replacing whatever was there. One at a time: the
   * child is a detail OF the draft, and two would compete for the same meaning (and
   * the same width) beside it.
   */
  function openChild(id: DraftKey, child: DraftZoneChildSpec): void {
    instances.value = instances.value.map((entry) =>
      entry.id === id ? { ...entry, child } : entry,
    );
  }

  function closeChild(id: DraftKey): void {
    instances.value = instances.value.map((entry) =>
      entry.id === id ? { ...entry, child: undefined } : entry,
    );
  }

  /**
   * "This one is already open": the click asked for a draft that exists, so instead of
   * a second panel the existing one rings — the zone's twin of the cascade's
   * re-trigger pulse. A counter per instance, so every repeat rings again.
   */
  const pulseCounts = ref<Record<DraftKey, number>>({});
  function pulseInstance(id: DraftKey): void {
    pulseCounts.value = { ...pulseCounts.value, [id]: (pulseCounts.value[id] ?? 0) + 1 };
  }

  function setInstanceMinimized(id: DraftKey, minimized: boolean): void {
    instances.value = instances.value.map((entry) =>
      entry.id === id
        ? // Collapsing takes the child with it: it is a detail of this draft, and a
          // 500px panel left beside a 52px rail would be an orphan pointing at
          // something the user just put away.
          { ...entry, minimized, child: minimized ? undefined : entry.child }
        : entry,
    );
  }

  // ---------------------------------------------------------------------------
  // Graduation: the ACTIVE create column → a zone instance
  // ---------------------------------------------------------------------------
  //
  // A zone type's create flow does NOT start in the zone. It starts as the one
  // active `:new` cascade column — in the URL, deep-linkable, and limited to one
  // at a time for free, because `cascadeReducer.open` answers a second identical
  // `{type,id}` with a re-trigger rather than a second column (ADR-0029 §G).
  //
  // It graduates into the zone the moment it would otherwise LEAVE, and only if it
  // holds work: another "Neu" needs the slot, the user changes page, a root-list
  // pick re-roots the board out from under it. Deliberately not on the first
  // keystroke — that would remount the panel mid-typing and throw the caret out of
  // the field the user is in.

  // The four maps this used to keep beside itself are the graduation policy's MEMORY
  // (ADR-0029 §H-6) and live there now: the page each active column was last seen on,
  // every identified column's props while they are still readable, what each panel calls
  // itself, and how many create columns of a type this session has minted. They are
  // memory about graduation, and graduation is what reads them.
  //
  // The label and factory services below still WRITE two of them, and reach them by
  // their full name rather than destructuring one out: `draftLabels.set(…)` on its own
  // reads as this store's own state, and it is not — a reader has to be able to see
  // whose memory is being moved.

  /**
   * A panel naming itself, e.g. `(Max Mustermann) Termin`. Called while mounted; the
   * last value is what a parked rail of it will show, because by then there is no
   * panel left to ask (see {@link DraftInstance.label}).
   */
  function noteDraftLabel(key: DraftKey, label: string): void {
    if (label) graduationMemory.draftLabels.set(key, label);
  }

  /**
   * What the panel filed under this key calls itself, if it has said. Read by the
   * rails a draft wears while it is still IN the cascade — collapsed rather than
   * parked — so a column that shrinks to 52px keeps the name it would carry once
   * parked, instead of falling back to its type's header.
   */
  function draftLabelOf(key: DraftKey | null | undefined): string {
    return key ? (graduationMemory.draftLabels.get(key) ?? '') : '';
  }

  /** The id the next create column of this type must carry. */
  function nextActiveColumnId(type: SidebarType): string {
    return mintNextColumnId(graduationMemory, type);
  }

  /**
   * Hand the active column's entered values to a new zone instance and return its
   * id, or `null` when there is nothing to hand over.
   *
   * It arrives **collapsed**. Graduation happens because the user moved on — another
   * "Neu", a page change, a row pick — so the work is being set aside, not carried
   * along, and the fresh form deserves the width. Three expanded 500px panels beside
   * a new one push the list off screen and make the user scroll to reach the thing
   * they just asked for. Parking is also the only rule that reads the same at all
   * three triggers instead of one behaviour per trigger.
   *
   * The values are not copied: `draftStateStore.rename` moves the buffer OBJECT, and
   * the panel's registered projection closes over that object rather than over its
   * key, so dirtiness never blinks off and no rail or dot flickers mid-move.
   *
   * The caller is responsible for getting the column out of the URL. Until it is,
   * two panels would share the moved buffer — the still-mounted column and the zone
   * twin — and a keystroke in either would show up in both.
   */
  function graduateActiveColumn(type: SidebarType): DraftKey | null {
    if (!isDraftDirty(type)) return null;
    const id = instanceDraftKey(type, ++idCounter);
    useDraftStateStore().rename(activeDraftKey(type), id);
    // Reconstructed rather than read off the live column: for a root create flow
    // (`createPatient` declares no `parentIds`) the id sentinel is the whole prop
    // bag, and the panel ignores its value anyway — everything that matters lives
    // in the buffer that just moved. A zone type WITH `parentIds` would need its
    // ancestor bindings snapshotted here instead (see §G on frozen props).
    const idProp = sidebars.find((sidebar) => sidebar.type === type)?.inline?.idProp;
    // Newest first: the zone is a stack rather than a queue, so a graduated draft
    // lands immediately right of the boundary and stays beside the fresh column that
    // took its place. Older drafts drift away from it.
    instances.value = [
      {
        id,
        type,
        routeName: graduationMemory.activeColumnRoutes.get(type) ?? currentRouteName.value,
        props: idProp ? { [idProp]: NEW_ID_SENTINEL } : null,
        minimized: true,
        // What the panel last called itself. A create column's own name is the only
        // thing that tells two parked ones apart — they share a type, a page and a
        // reconstructed prop bag.
        label: graduationMemory.draftLabels.get(activeDraftKey(type)),
      },
      ...instances.value,
    ];
    graduationMemory.activeColumnRoutes.delete(type);
    graduationMemory.draftLabels.delete(activeDraftKey(type));
    return id;
  }

  /**
   * The same handover for an IDENTIFIED column (`DraftDefinition.keyFor`): the panel
   * is not a create flow, so nothing replaces it — it simply stopped being in the
   * cascade, and whatever the user answered has to keep existing somewhere.
   *
   * Its props are the ones it really had, not a reconstructed sentinel: a
   * questionnaire without its patient id is a different questionnaire, and the zone
   * has no ancestor column to read one from. They arrive in the policy's own record of
   * the column, taken while it was still standing (§H-6).
   */
  function graduateIdentifiedColumn(record: RecordedSighting): DraftKey | null {
    const identityKey = record.key;
    if (!isDraftDirty(record.type, identityKey)) {
      // Nothing answered, so nothing to park — but the buffer still holds the step the
      // user had reached, and leaving that behind would reopen the form part-way
      // through with no answers in it.
      discard(identityKey);
      graduationMemory.draftLabels.delete(identityKey);
      return null;
    }
    const id = instanceDraftKey(record.type, ++idCounter);
    useDraftStateStore().rename(identityKey, id);
    instances.value = [
      {
        id,
        type: record.type,
        routeName: record.routeName,
        props: record.props,
        minimized: true,
        identityKey,
        // LIVE read first, the sighting's copy second. The copy is only as fresh as the
        // last pass of the rule, and a name resolves from queries — so a Praxis that
        // opened a questionnaire and had the patient's name land a moment later would
        // otherwise park a rail called "Formular ausfüllen".
        label: graduationMemory.draftLabels.get(identityKey) ?? record.label,
      },
      ...instances.value,
    ];
    return id;
  }

  /** The parked draft holding this identity, if one does. */
  function instanceForIdentity(identityKey: DraftKey): DraftInstance | undefined {
    return instances.value.find((entry) => entry.identityKey === identityKey);
  }

  /**
   * Catch every way the active column can leave the URL *without* going through
   * `openSidebar` — a page change, a root-list pick, an ancestor's ✕, a hand-edited
   * URL. Whatever the cause, a column that held work and is now gone graduates.
   *
   * Nothing but wiring: the rule itself is `planGraduation` (§H-6), which decides
   * which identities have gone stale and which types have lost their column. What is
   * left here is the half that needs a store — asking whether a draft holds work,
   * minting the instance, moving the buffer.
   *
   * Declared BEFORE the page-change settler below so a draft that graduates
   * because the user left the page is then collapsed by that pass — it comes back
   * as a rail, like every other parked draft.
   */
  const survey = () => surveyDraftColumns(useCascadeStore().stack, drafts);
  watch(
    // A signal string rather than the survey, so an unrelated cascade change — a row
    // pick, a drill-in, an ancestor closing — does not re-run the rule. Router-guarded
    // because a watcher runs its getter EAGERLY, and nothing else in this store demands
    // a router at construction time.
    () => (isRouterRegistered() ? graduationSignal(survey()) : ''),
    () => {
      // The signal's own guard cannot cover the handler: `immediate: true` runs it once
      // at construction, and reading the page name there would demand a router this
      // store has never demanded.
      if (!isRouterRegistered()) return;
      const plan = planGraduation({
        survey: survey(),
        routeName: currentRouteName.value,
        zoneAvailable: draftZoneAvailable(),
        definitions: drafts,
        memory: graduationMemory,
      });
      for (const record of plan.staleIdentities) graduateIdentifiedColumn(record);
      for (const type of plan.graduatingTypes) {
        if (graduateActiveColumn(type)) continue;
        // Nothing worth parking — but the buffer can still hold POSITION (the
        // Verordnung's chosen Fall, which counts for nothing by design), and leaving it
        // under the active key reopened the next create on the old form instead of the
        // picker (TM-9616). Buffer only, like the page-change settler's pristine pass:
        // a clean buffer does not mean `onDiscard`'s stored answers are unwanted.
        //
        // Only for a column that really stood in the cascade and has now LEFT it —
        // `graduatingTypes` also names every type that never had one, and an overlay
        // holding the active key is not a column. And only HERE: the "Neu" re-trigger in
        // `sidebarStore` asks `graduateActiveColumn` the same question while the column
        // is still mounted, and resetting there would pull its buffer out from under it.
        if (!graduationMemory.activeColumnRoutes.delete(type)) continue;
        useDraftStateStore().reset(activeDraftKey(type));
        graduationMemory.draftLabels.delete(activeDraftKey(type));
      }
    },
    { immediate: true },
  );

  /**
   * Leaving the page settles every instance: the pristine ones are destroyed and
   * the ones holding work are collapsed, so returning shows rails over the list
   * rather than three forms covering it.
   *
   * Keyed on `route.name`, so drilling around inside the same page — which
   * changes the cascade path but not the name — leaves the zone completely alone.
   * That is the whole contract of the zone: only the user moves it, never the
   * navigation.
   *
   * Wiring again: `planSettle` (§H-6) says which instances to destroy and which to
   * park, and an empty plan is what leaves the array — and every object in it —
   * untouched.
   */
  // The source is guarded rather than `currentRouteName` itself: a watcher runs its
  // getter EAGERLY, when the store is built, and every other read here is lazy. Left
  // unguarded, merely instantiating this store before the router is registered would
  // throw — which is a real constraint the store never had, and one no caller could
  // see coming.
  watch(
    () => (isRouterRegistered() ? currentRouteName.value : ''),
    () => {
      const plan = planSettle({
        instances: instances.value,
        dirtyIds: dirtyInstanceIds.value,
      });
      if (plan.destroy.length === 0 && plan.park.length === 0) return;
      // The destroyed instances' buffers would otherwise outlive them and be handed to
      // the next instance that happens to mint the same key — it cannot today (the
      // counter never rewinds), but a leaked buffer is a leak either way.
      const draftState = useDraftStateStore();
      for (const key of plan.destroy) draftState.reset(key);
      const destroyed = new Set(plan.destroy);
      const parked = new Set(plan.park);
      instances.value = instances.value
        .filter((entry) => !destroyed.has(entry.id))
        .map((entry) =>
          parked.has(entry.id) ? { ...entry, minimized: true, child: undefined } : entry,
        );
    },
  );

  return {
    discard,
    hasDraftOnRoutes,
    // Draft zone (ADR-0029 §G)
    instances,
    remove,
    dirtyInstanceIds,
    activeDraftColumnIndex,
    zoneInstances,
    noteDraftLabel,
    draftLabelOf,
    ordinalOf,
    labelFor,
    setInstanceMinimized,
    pulseCounts,
    pulseInstance,
    openChild,
    closeChild,
    graduateActiveColumn,
    nextActiveColumnId,
    instanceForIdentity,
    // Re-exported so callers don't need a second import to ask "is this type a
    // draft type at all?".
    definitions: drafts,
  };
});
